import { Box, BoxProps } from '@mui/material';
import { useEffect, useRef, useState } from 'react';

interface NotificationFadeInProps extends BoxProps {
    children: React.ReactNode;
    delay?: number;
    direction?: 'up' | 'down' | 'left' | 'right' | 'none';
    distance?: number;
    duration?: number;
    threshold?: number;
    initialScale?: number;
}

export function NotificationFadeIn({
    children,
    delay = 0,
    direction = 'up',
    distance = 30,
    duration = 500,
    threshold = 0.1,
    initialScale = 0.8,
    ...boxProps
}: NotificationFadeInProps) {
    const [isVisible, setIsVisible] = useState(false);
    const ref = useRef<HTMLDivElement>(null);

    useEffect(() => {
        const observer = new IntersectionObserver(
            ([entry]) => {
                if (entry.isIntersecting) {
                    setIsVisible(true);
                    observer.disconnect();
                }
            },
            { threshold }
        );

        if (ref.current) {
            observer.observe(ref.current);
        }

        return () => observer.disconnect();
    }, [threshold]);

    const getTransform = () => {
        if (isVisible) return 'none';
        const scalePart = `scale(${initialScale})`;

        switch (direction) {
            case 'up':
                return `translateY(${distance}px) ${scalePart}`;
            case 'down':
                return `translateY(-${distance}px) ${scalePart}`;
            case 'left':
                return `translateX(${distance}px) ${scalePart}`;
            case 'right':
                return `translateX(-${distance}px) ${scalePart}`;
            case 'none':
                return `translate(0, 0) ${scalePart}`;
        }
    };

    return (
        <Box
            ref={ref}
            {...boxProps}
            sx={{
                opacity: isVisible ? 1 : 0,
                transform: getTransform(),
                transition: `
          opacity ${duration}ms cubic-bezier(0.4, 0, 0.2, 1) ${delay}ms, 
          transform ${duration}ms cubic-bezier(0.34, 1.56, 0.64, 1) ${delay}ms
        `, // Using a slight overshoot curve for the "pop" effect on transform
                ...boxProps.sx,
            }}
        >
            {children}
        </Box>
    );
}
